// src/components/Popups/EditReservation/cancellationStartTime.js
//
// Cand o lectie e trecuta in „Anulari", ea ramane in calendar ca istoric, dar
// slotul trebuie sa redevina liber: altfel o lectie noua pe aceeasi ora e
// respinsa de server cu „instructorul este deja ocupat". Conventia e sa mutam
// rezervarea anulata cu un minut peste eticheta slotului (10:00 -> 10:01), iar
// la restaurare sa o aducem inapoi pe slot.
//
// Regula e idempotenta si se auto-repara: orice salvare a unei rezervari
// anulate ramase fix pe slot (anulari facute inainte de aceasta conventie sau
// din alt canal) ii elibereaza slotul.

import {
   BUSY_KEYS_MODE,
   busyLocalKeyFromStored,
   isoForDbMatchLocalHour,
   toUtcIsoFromMoldova,
} from "../../../utils/moldovaTime";
import { LESSON_SLOT_LABELS } from "../../../constants/lessonSlots";

export const CANCELLATION_OFFSET_MINUTES = 1;

const SLOT_LABEL_SET = new Set(LESSON_SLOT_LABELS);

function localDateObjFromStr(value) {
   const [y, m, d] = String(value || "").split("-").map(Number);
   if (!Number.isFinite(y)) return null;
   return new Date(y, (m || 1) - 1, d || 1, 0, 0, 0, 0);
}

export function shiftLocalKeyByMinutes(localKey, deltaMinutes) {
   const rawKey = String(localKey || "");
   if (!rawKey) return "";

   const minutesDelta = Math.trunc(Number(deltaMinutes) || 0);
   if (!minutesDelta) return rawKey;

   const [datePart, timePart] = rawKey.split("|");
   if (!datePart || !timePart) return rawKey;

   const [yy, mm, dd] = datePart.split("-").map(Number);
   const [hh, mi] = timePart.split(":").map(Number);
   if (
      !Number.isFinite(yy) ||
      !Number.isFinite(mm) ||
      !Number.isFinite(dd) ||
      !Number.isFinite(hh) ||
      !Number.isFinite(mi)
   ) {
      return rawKey;
   }

   const pivot = new Date(Date.UTC(yy, mm - 1, dd, hh, mi, 0, 0));
   if (Number.isNaN(pivot.getTime())) return rawKey;
   pivot.setUTCMinutes(pivot.getUTCMinutes() + minutesDelta);

   const y = String(pivot.getUTCFullYear()).padStart(4, "0");
   const m = String(pivot.getUTCMonth() + 1).padStart(2, "0");
   const d = String(pivot.getUTCDate()).padStart(2, "0");
   const h = String(pivot.getUTCHours()).padStart(2, "0");
   const min = String(pivot.getUTCMinutes()).padStart(2, "0");
   return `${y}-${m}-${d}|${h}:${min}`;
}

export function storedIsoFromBusyLocalKey(localKey) {
   const rawKey = String(localKey || "");
   if (!rawKey) return null;
   const [dayStr, hhmm] = rawKey.split("|");
   if (!dayStr || !hhmm) return null;

   const localDay = localDateObjFromStr(dayStr);
   if (!localDay || Number.isNaN(localDay.getTime())) return null;

   const utcIso = toUtcIsoFromMoldova(localDay, hhmm);
   if (!utcIso) return null;
   return BUSY_KEYS_MODE === "local-match"
      ? isoForDbMatchLocalHour(utcIso)
      : utcIso;
}

export function shiftStoredStartByMinutes(storedStart, deltaMinutes) {
   const raw = String(storedStart || "");
   if (!raw) return null;

   const localKey = busyLocalKeyFromStored(raw);
   const shiftedLocalKey = shiftLocalKeyByMinutes(localKey, deltaMinutes);
   return storedIsoFromBusyLocalKey(shiftedLocalKey) || raw;
}

function localHHMMFromStored(storedStart) {
   const raw = String(storedStart || "");
   if (!raw) return "";
   const [, hhmm = ""] = String(busyLocalKeyFromStored(raw) || "").split("|");
   return hhmm;
}

function shiftHHMMByMinutes(hhmm, deltaMinutes) {
   const raw = String(hhmm || "");
   if (!raw) return "";

   const [hhRaw, mmRaw] = raw.split(":").map(Number);
   if (!Number.isFinite(hhRaw) || !Number.isFinite(mmRaw)) return "";

   const pivot = new Date(Date.UTC(2000, 0, 1, hhRaw, mmRaw, 0, 0));
   if (Number.isNaN(pivot.getTime())) return "";
   pivot.setUTCMinutes(
      pivot.getUTCMinutes() + Math.trunc(Number(deltaMinutes) || 0),
   );

   const hh = String(pivot.getUTCHours()).padStart(2, "0");
   const mm = String(pivot.getUTCMinutes()).padStart(2, "0");
   return `${hh}:${mm}`;
}

/** Ora stocata cade exact pe o eticheta de slot, deci ocupa caseta din grila. */
export function isSlotAlignedStart(storedStart) {
   const hhmm = localHHMMFromStored(storedStart);
   return !!hhmm && SLOT_LABEL_SET.has(hhmm);
}

/** Ora stocata e „slot + 1 minut", adica poarta deja marcajul de anulare. */
export function hasCancellationPlusOneOffset(storedStart) {
   const hhmm = localHHMMFromStored(storedStart);
   if (!hhmm) return false;

   for (const base of LESSON_SLOT_LABELS) {
      if (shiftHHMMByMinutes(base, CANCELLATION_OFFSET_MINUTES) === hhmm) {
         return true;
      }
   }
   return false;
}

export function resolveStartTimeForCancellationTransition({
   existingStartTime,
   selectedStartTime,
   wasCancelled,
   willBeCancelled,
   changingTime,
}) {
   const baseStartTime = changingTime
      ? String(selectedStartTime || "")
      : String(existingStartTime || "");
   if (!baseStartTime) return null;

   if (willBeCancelled) {
      if (changingTime || !wasCancelled) {
         return shiftStoredStartByMinutes(
            baseStartTime,
            CANCELLATION_OFFSET_MINUTES,
         );
      }
      // Era deja anulata, dar a ramas fix pe slot: o mutam acum, ca slotul sa
      // se elibereze la prima salvare.
      return isSlotAlignedStart(baseStartTime)
         ? shiftStoredStartByMinutes(baseStartTime, CANCELLATION_OFFSET_MINUTES)
         : baseStartTime;
   }

   if (wasCancelled) {
      if (changingTime) return baseStartTime;
      if (!hasCancellationPlusOneOffset(baseStartTime)) return baseStartTime;
      return shiftStoredStartByMinutes(
         baseStartTime,
         -CANCELLATION_OFFSET_MINUTES,
      );
   }

   return baseStartTime;
}

/**
 * Acelasi moment in timp, indiferent de format: ora trimisa spre backend e
 * scrisa cu offset („...T13:01:00+03:00"), iar cea primita inapoi in UTC
 * („...T10:01:00.000Z") — aceeasi clipa, doua siruri diferite.
 */
export function isSameStoredStart(a, b) {
   const textA = String(a || "");
   const textB = String(b || "");
   if (textA === textB) return true;

   const msA = new Date(textA).getTime();
   const msB = new Date(textB).getTime();
   if (!Number.isFinite(msA) || !Number.isFinite(msB)) return false;
   return msA === msB;
}
